import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPut, newRequestId } from '@/api/client';
import { queryKeys } from '@/api/keys';
import { LIVE_POLL_INTERVAL_MS } from '@/lib/freshness';
import { livePollIntervalMs } from '@/api/queries/polling';
import {
  alertDetailSchema,
  alertDeliveryListSchema,
  alertListSchema,
  alertRuleListSchema,
  alertRuleSchema,
  alertRuleTestResultSchema,
  notificationGroupSchema,
  type AlertDetail,
} from '@/api/schemas/alerts';

export interface AlertListParams {
  state?: 'open' | 'acknowledged' | 'resolved';
  category?: string;
  severity?: string;
  zoneId?: string;
  from?: string;
  to?: string;
  limit?: number;
  page?: number;
  pageSize?: number;
}

export function useAlerts(params: AlertListParams = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.alerts.list({ ...params }),
    queryFn: async () => alertListSchema.parse(await apiGet('/alerts', { query: { ...params } })),
    enabled,
    refetchInterval: () => livePollIntervalMs(),
    staleTime: LIVE_POLL_INTERVAL_MS,
  });
}

/** The top bar's alert dot: the live count of open alerts. */
export function useOpenAlertCount(enabled = true) {
  const query = useAlerts({ state: 'open', pageSize: 1 }, enabled);
  return { ...query, count: query.data?.totalCount ?? 0 };
}

export function useAlertDetail(id: string | null) {
  return useQuery<AlertDetail>({
    queryKey: queryKeys.alerts.detail(id ?? ''),
    queryFn: async () => alertDetailSchema.parse(await apiGet(`/alerts/${id}`)),
    enabled: id !== null,
    staleTime: LIVE_POLL_INTERVAL_MS,
  });
}

/**
 * **Acknowledge is not resolve** (§9.8).
 *
 * Acknowledging says "I have seen this": it stops the escalation timer,
 * records who and when, and leaves the alert open. Resolving says "it is
 * fixed". They are separate calls because they are separate claims, and
 * conflating them is how an alert stops being evidence of anything.
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      alertDetailSchema.parse(
        await apiPost(`/alerts/${id}/acknowledge`, {
          // The mobile app queues acknowledgements through its outbox, so the
          // idempotency key is part of the contract rather than a nicety.
          body: { note, clientRequestId: newRequestId() },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

/** Bulk acknowledge. After a gateway outage there may be sixty. */
export function useBulkAcknowledgeAlerts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ alertIds, note }: { alertIds: string[]; note?: string }) =>
      apiPost('/alerts/acknowledge', {
        body: { alertIds, note, clientRequestId: newRequestId() },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

/**
 * Resolve. **A note is required when the severity was major or critical** —
 * somebody will ask what was actually done, and "resolved" is not an answer.
 * There is deliberately no bulk variant: sixty bays do not get fixed by one
 * click, and offering the button would invite pretending they did.
 */
export function useResolveAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      alertDetailSchema.parse(
        await apiPost(`/alerts/${id}/resolve`, {
          body: { note, clientRequestId: newRequestId() },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

/** Severities for which the server refuses a resolve without a note. */
export const NOTE_REQUIRED_SEVERITIES = ['major', 'critical'] as const;

export function resolveNeedsNote(severity: string): boolean {
  return (NOTE_REQUIRED_SEVERITIES as readonly string[]).includes(severity);
}

/* ── Alert rules ─────────────────────────────────────────────────────────── */

export function useAlertRules(enabled = true) {
  return useQuery({
    queryKey: queryKeys.alerts.rules(),
    queryFn: async () => alertRuleListSchema.parse(await apiGet('/alert-rules')).items,
    enabled,
    staleTime: 60_000,
  });
}

export function useNotificationGroups(enabled = true) {
  return useQuery({
    queryKey: queryKeys.alerts.notificationGroups(),
    queryFn: async () =>
      z
        .object({ items: z.array(notificationGroupSchema) })
        .parse(await apiGet('/notification-groups')).items,
    enabled,
    staleTime: 60_000,
  });
}

/** What was sent for one alert (needs `alerts.configure`, like the rest of the delivery surface). */
export function useAlertDeliveries(id: string | null, enabled = true) {
  return useQuery({
    queryKey: ['alerts', 'deliveries', id ?? ''] as const,
    queryFn: async () =>
      alertDeliveryListSchema.parse(await apiGet(`/alerts/${id}/deliveries`)).items,
    enabled: enabled && id !== null,
    staleTime: LIVE_POLL_INTERVAL_MS,
  });
}

/** Every rule change writes a **before/after** audit row — W-6.2f. */
export function useUpdateAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      alertRuleSchema.parse(await apiPut(`/alert-rules/${id}`, { body: patch })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules'] }),
  });
}

/**
 * Replay the rule over the last seven days. **Reports what it would do and
 * raises nothing** — the single cheapest defence against an alert storm on
 * day one, and the thing that stops a threshold being agreed in a meeting
 * and discovered to be wrong in production.
 */
export function useTestAlertRule() {
  return useMutation({
    mutationFn: async (id: string) =>
      alertRuleTestResultSchema.parse(await apiPost(`/alert-rules/${id}/test`, { body: {} })),
  });
}
